import React, { useEffect, useRef } from "react";
import { Modal, Button, Table } from "react-bootstrap";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// Collect vertical break-point candidates (in captured-canvas px) so A4 pages
// never cut through a table row or an Additional Information box.
const collectPageBreakPoints = (node, scale) => {
  const offsets = [];
  const rootTop = node.getBoundingClientRect().top;
  node.querySelectorAll("tr, .p-3.mb-3").forEach((el) => {
    const top = el.getBoundingClientRect().top - rootTop;
    if (top > 0) offsets.push(top * scale);
  });
  return offsets.sort((a, b) => a - b);
};

const ViewQuoteDetailModal = ({
  show,
  onHide,
  leadId,
  legalFeeData,
  leadData,
  calculationTotals,
}) => {
  console.log("🔍 ViewQuoteDetailModal - legalFeeData:", legalFeeData);
  console.log("🔍 ViewQuoteDetailModal - leadData:", leadData);
  console.log(
    "🎯 SPECIFIC CHECK - legalFeeData.legal_fee:",
    legalFeeData?.legal_fee,
  );
  console.log(
    "🎯 SPECIFIC CHECK - typeof legalFeeData.legal_fee:",
    typeof legalFeeData?.legal_fee,
  );
  console.log("🏠 Service Type:", leadData?.service_type);
  console.log(
    "💰 Help to Buy ISA - leadData.help_to_buy_isa:",
    leadData?.info?.helpToBuyISA,
  );
  console.log(
    "💰 Help to Buy ISA Fee from API:",
    legalFeeData?.help_to_buy_isa_fee,
  );

  // AUCTION/REPOSSESSION FEE DEBUG
  console.log("🎪 === AUCTION/REPOSSESSION FEE DEBUG ===");
  console.log(
    "🎪 auction_repossession_fee from API:",
    legalFeeData?.auction_repossession_fee,
  );
  console.log(
    "🎪 auction_repossession_fee_label:",
    legalFeeData?.auction_repossession_fee_label,
  );
  console.log("🎪 all_fixed_fees:", legalFeeData?.all_fixed_fees);
  console.log(
    "🎪 auction fee in all_fixed_fees:",
    legalFeeData?.all_fixed_fees?.find(
      (fee) => fee.label === "auctionRepossessionFee",
    ),
  );
  console.log(
    "🎪 leadData.auction_repossession:",
    leadData?.info?.auctionRepossession,
  );
  console.log("🎪 === END AUCTION FEE DEBUG ===");

  console.log("📋 Full leadData structure:", JSON.stringify(leadData, null, 2));

  // Debug log to see what values are being used in the table
  if (show && legalFeeData) {
    console.log("📊 TABLE VALUES:");
    console.log("Legal Fee NET:", legalFeeData.legal_fee);
    console.log("TT Fee NET:", legalFeeData.tt_fee);
    console.log("Identity Check NET:", legalFeeData.identity_check);
    console.log("Stamp Duty NET:", legalFeeData.stamp_duty);
    if (leadData?.service_type === "Sale") {
      console.log("💰 Sale - Official Copies:", legalFeeData.official_copies);
      console.log(
        "💰 Sale - Mortgage Admin Fee:",
        legalFeeData.mortgage_admin_fee,
      );
    }
  }

  // Show loading if data is not available
  const isDataLoading = false; // Disable loading state - always show data

  // Ref to the rendered quote content used to build the download PDF.
  const quoteContentRef = useRef(null);

  const formatCurrency = (amount) => {
    const value = Number(amount) || 0;
    console.log("formatCurrency input:", amount, "output:", value);
    return `£${value.toFixed(2)}`;
  };

  // Calculate Stamp Duty Land Tax based on purchase price (tiered)
  const calculateStampDutyLandTax = (purchasePrice) => {
    if (!purchasePrice || purchasePrice <= 0) return 0;

    const price = Number(purchasePrice);
    let stampDuty = 0;

    // Tiered calculation
    if (price > 1500000) {
      // Over £1,500,000: 12% on amount above £1,500,000
      stampDuty += (price - 1500000) * 0.12;
      stampDuty += (1500000 - 925000) * 0.1;
      stampDuty += (925000 - 250000) * 0.05;
      stampDuty += (250000 - 125000) * 0.02;
      stampDuty += 125000 * 0;
    } else if (price > 925000) {
      // £925,001 to £1,500,000: 10% on amount above £925,000
      stampDuty += (price - 925000) * 0.1;
      stampDuty += (925000 - 250000) * 0.05;
      stampDuty += (250000 - 125000) * 0.02;
      stampDuty += 125000 * 0;
    } else if (price > 250000) {
      // £250,001 to £925,000: 5% on amount above £250,000
      stampDuty += (price - 250000) * 0.05;
      stampDuty += (250000 - 125000) * 0.02;
      stampDuty += 125000 * 0;
    } else if (price > 125000) {
      // £125,001 to £250,000: 2% on amount above £125,000
      stampDuty += (price - 125000) * 0.02;
      stampDuty += 125000 * 0;
    } else {
      // Up to £125,000: 0%
      stampDuty = 0;
    }

    return Math.round(stampDuty * 100) / 100;
  };

  const downloadQuote = async () => {
    const node = quoteContentRef.current;
    if (!node) return;

    try {
      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      });

      const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;

      // Scale captured pixels to mm so the content fills the A4 width.
      const pxToMm = usableWidth / canvas.width;
      const pageHeightPx = usableHeight / pxToMm;

      // Row-aligned page breaks: snap page slices to table rows / info boxes.
      const breakPoints = collectPageBreakPoints(
        node,
        canvas.width / node.clientWidth,
      );

      let top = 0;
      let page = 0;
      while (top < canvas.height) {
        let bottom = Math.min(top + pageHeightPx, canvas.height);

        // Snap the slice bottom to a nearby break point so rows stay intact.
        if (bottom < canvas.height) {
          const minBreak = top + pageHeightPx * 0.5;
          const candidates = breakPoints.filter(
            (bp) => bp > minBreak && bp <= bottom,
          );
          if (candidates.length > 0) {
            bottom = candidates[candidates.length - 1];
          }
        }

        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = Math.max(1, Math.ceil(bottom - top));
        const ctx = slice.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(
          canvas,
          0,
          top,
          canvas.width,
          slice.height,
          0,
          0,
          canvas.width,
          slice.height,
        );

        if (page > 0) doc.addPage();
        doc.addImage(
          slice.toDataURL("image/png"),
          "PNG",
          margin,
          margin,
          usableWidth,
          slice.height * pxToMm,
        );

        top = bottom;
        page += 1;
      }

      doc.save(
        `Quote_${leadData?.lead_id || leadId || "Unknown"}_${new Date().getTime()}.pdf`,
      );
    } catch (err) {
      console.error("PDF generation failed:", err);
    }
  };

  // Static data as per screenshot
  // Use dynamic data from API if available, otherwise fallback to static
  // Use legalFeeData if available, otherwise use leadData values
  // Check for both legal_fee and legal_fees (in case API returns with 's')
  // Get legal fee from API response - prioritize legalFeeData from API

  // Get legal fee from API response (already calculated based on service_type)
  // Try multiple paths: api_response.fees.legalFee.amount > legal_fee > leadData
  const legalFee = Number(
    legalFeeData?.api_response?.fees?.legalFee?.amount ||
      legalFeeData?.fees?.legalFee?.amount ||
      legalFeeData?.legal_fee ||
      leadData?.legal_fee ||
      0,
  );

  const legalFeeVAT = Number(
    legalFeeData?.api_response?.fees?.legalFee?.vat ||
      legalFeeData?.fees?.legalFee?.vat ||
      legalFee * 0.2,
  );
  const legalFeeGross = Number(
    legalFeeData?.api_response?.fees?.legalFee?.gross ||
      legalFeeData?.fees?.legalFee?.gross ||
      legalFee + legalFeeVAT,
  );

  console.log("💰 LEGAL FEE CALCULATION:");
  console.log("legalFeeData:", legalFeeData);
  console.log("FINAL legalFee value:", legalFee);
  console.log("Formatted legalFee for display:", formatCurrency(legalFee));

  const ttFee = Number(
    legalFeeData?.api_response?.fees?.ttFee?.amount ||
      legalFeeData?.fees?.ttFee?.amount ||
      legalFeeData?.tt_fee ||
      leadData?.tt_fee ||
      30,
  );
  const ttFeeVAT = Number(
    legalFeeData?.api_response?.fees?.ttFee?.vat ||
      legalFeeData?.fees?.ttFee?.vat ||
      ttFee * 0.2,
  );
  const ttFeeGross = Number(
    legalFeeData?.api_response?.fees?.ttFee?.gross ||
      legalFeeData?.fees?.ttFee?.gross ||
      ttFee + ttFeeVAT,
  );

  // Get purchaser count from leadData
  const purchaserCount =
    leadData?.customer_ids?.length || leadData?.customers?.length || 1;
  console.log("👥 Purchaser Count for Identity Check:", purchaserCount);

  // Calculate Identity Check - multiply by purchaser count
  const identityCheckBase = Number(
    legalFeeData?.api_response?.fees?.identityCheck?.amount ||
      legalFeeData?.fees?.identityCheck?.amount ||
      legalFeeData?.identity_check ||
      leadData?.identity_check ||
      10,
  );
  const identityCheck = identityCheckBase * purchaserCount;
  const identityCheckVAT = Number(
    legalFeeData?.api_response?.fees?.identityCheck?.vat ||
      legalFeeData?.fees?.identityCheck?.vat ||
      identityCheck * 0.2, // ✅ Identity Check has 20% VAT
  );
  const identityCheckGross = Number(
    legalFeeData?.api_response?.fees?.identityCheck?.gross ||
      legalFeeData?.fees?.identityCheck?.gross ||
      identityCheck + identityCheckVAT,
  );

  console.log("🔍 Identity Check Calculation:");
  console.log("  - Base Identity Check:", identityCheckBase);
  console.log("  - Purchaser Count:", purchaserCount);
  console.log("  - Total Identity Check (Base × Count):", identityCheck);

  // Helper function to check if value is truthy (true, "Yes", 1)
  const isTruthy = (value) => {
    if (value === true || value === 1 || value === "1") return true;
    if (typeof value === "string" && value.toLowerCase() === "yes") return true;
    return false;
  };

  // Helper function to get fee value and calculate VAT
  const getFeeWithVAT = (feeKey, hasVAT = true) => {
    // Try multiple paths to find the fee data
    // 1. Try legalFeeData.api_response.fees.camelCaseKey (from calculation document)
    let feeData = legalFeeData?.api_response?.fees?.[feeKey];

    // 2. Try legalFeeData.fees.camelCaseKey (direct API response)
    if (!feeData) {
      feeData = legalFeeData?.fees?.[feeKey];
    }

    if (feeData && typeof feeData === "object") {
      // API returns {amount, vat, gross, label} structure
      return {
        net: Number(feeData.amount || 0),
        vat: Number(feeData.vat || 0),
        gross: Number(feeData.gross || 0),
      };
    }

    // 3. Fallback to snake_case root level keys
    // Convert camelCase to snake_case for lookup
    const snakeKey = feeKey.replace(/([A-Z])/g, "_$1").toLowerCase();
    const net = Number(
      legalFeeData?.[snakeKey] ||
        legalFeeData?.[feeKey] ||
        leadData?.[snakeKey] ||
        leadData?.[feeKey] ||
        0,
    );
    const vat = hasVAT ? net * 0.2 : 0;
    const gross = net + vat;
    return { net, vat, gross };
  };

  // All Fixed Fees - using camelCase keys from API response
  const mortgageAdminFee = getFeeWithVAT("mortgageAdminFee");
  const helpToBuyISAFee = getFeeWithVAT("help_to_buy_isa_fee");
  const helpToBuyFee = getFeeWithVAT("helpToBuyFee");
  // const helpToBuyEquityLoanFee = getFeeWithVAT('helpToBuyEquityLoanFee'); // ❌ REMOVED
  const rightToBuyFee = getFeeWithVAT("rightToBuyFee");
  // ❌ REMOVED: firstTimeBuyerFee - not a separate fee, first-time buyer affects SDLT only
  const islamicMortgageFee = getFeeWithVAT("islamicMortgageFee");
  const giftedDepositFeeFirstGiftor = getFeeWithVAT(
    "giftedDepositFeeFirstGiftor",
  ); // ✅ FIXED: Use correct API key
  const buyToLetFee = getFeeWithVAT("buyToLetFee");
  const sharedOwnershipFee = getFeeWithVAT("sharedOwnershipFee");
  const newBuildFee = getFeeWithVAT("newBuildFee");
  const auctionRepossessionFee = getFeeWithVAT("auctionRepossessionFee");
  const arrangingIndemnityPolicy = getFeeWithVAT("arrangingIndemnityPolicy");
  const leaseholdFee = getFeeWithVAT("leaseholdFee");

  // Disbursements - using camelCase keys from API response
  const hmLandRegistryFee = getFeeWithVAT("hmLandRegistryFee", false); // Government fee - no VAT
  const landRegistrySearch = getFeeWithVAT("landRegistrySearch", false); // Government fee - no VAT
  const landChargesSearch = getFeeWithVAT("landChargesSearch", true); // ✅ Taxable service - 20% VAT
  const searchPack = getFeeWithVAT("searchPack", false); // ❌ Third-party cost - NO VAT
  const officialCopies = getFeeWithVAT("officialCopies", false); // Government fee - no VAT

  // Get Stamp Duty - try from API first, then calculate from property value
  const propertyValue = leadData?.property_value || 0;

  // Try to get from API response first
  const stampDutyFromAPI = Number(
    legalFeeData?.api_response?.fees?.stampDuty?.amount ||
      legalFeeData?.fees?.stampDuty?.amount ||
      legalFeeData?.stamp_duty ||
      0,
  );

  // If not in API, calculate from property value (tiered calculation)
  const calculatedStampDuty =
    stampDutyFromAPI > 0
      ? stampDutyFromAPI
      : calculateStampDutyLandTax(propertyValue);
  console.log(
    "💷 Stamp Duty:",
    calculatedStampDuty,
    stampDutyFromAPI > 0 ? "(from API)" : "(calculated)",
  );

  // Create stampDuty object
  const stampDuty = {
    net: calculatedStampDuty,
    vat: 0, // Stamp Duty has 0% VAT
    gross: calculatedStampDuty,
  };

  console.log("📦 All Fees Loaded:", {
    legalFee,
    ttFee: { net: ttFee },
    identityCheck: { net: identityCheck },
    mortgageAdminFee,
    helpToBuyISAFee,
    helpToBuyFee,
    rightToBuyFee,
    islamicMortgageFee,
    giftedDepositFeeFirstGiftor,
    buyToLetFee,
    sharedOwnershipFee,
    newBuildFee,
    auctionRepossessionFee,
    arrangingIndemnityPolicy,
    leaseholdFee,
    stampDuty,
    hmLandRegistryFee,
    landRegistrySearch,
    landChargesSearch,
    searchPack,
    officialCopies,
  });

  // 🔍 DEBUG: Help to Buy ISA Fee
  console.log("🔍 Help to Buy ISA Fee Debug:");
  console.log("  - helpToBuyISAFee value:", helpToBuyISAFee);
  console.log(
    "  - legalFeeData.fees?.helpToBuyISAFee:",
    legalFeeData?.fees?.helpToBuyISAFee,
  );
  console.log(
    "  - legalFeeData.api_response?.fees?.helpToBuyISAFee:",
    legalFeeData?.api_response?.fees?.helpToBuyISAFee,
  );
  console.log("  - leadData.info?.helpToBuyISA:", leadData?.info?.helpToBuyISA);
  console.log("  - leadData.help_to_buy_isa:", leadData?.help_to_buy_isa);

  // Gifted Deposit Fee - only first giftor fee (no additional giftor fees)
  const purchaseInfo =
    leadData?.purchase_details_info || leadData?.purchasePropertyDetails;
  const giftedDepositCount =
    Number(purchaseInfo?.gifted_deposit || leadData?.info?.giftedDeposit) || 0;
  let giftedDepositFee = 0;
  if (giftedDepositCount > 0) {
    giftedDepositFee = giftedDepositFeeFirstGiftor.net;
  }
  const giftedDepositFeeVAT = giftedDepositFee * 0.2;
  const giftedDepositFeeGross = giftedDepositFee + giftedDepositFeeVAT;

  // 🎯 Use totals from database if available, otherwise calculate
  let totalNet, totalVAT, totalGross;

  if (calculationTotals) {
    // Use totals from leads_calculations table (database)
    console.log("💾 Using totals from DATABASE:", calculationTotals);
    // Check if calculationTotals has nested total object or direct properties
    if (calculationTotals.total) {
      totalNet = calculationTotals.total.net || 0;
      totalVAT = calculationTotals.total.vat || 0;
      totalGross = calculationTotals.total.gross || 0;
    } else {
      totalNet = calculationTotals.net || 0;
      totalVAT = calculationTotals.vat || 0;
      totalGross = calculationTotals.gross || 0;
    }
    console.log(
      "💾 Extracted totals - Net:",
      totalNet,
      "VAT:",
      totalVAT,
      "Gross:",
      totalGross,
    );
  } else {
    // Fallback: Calculate totals (old logic)
    console.log("🔢 Calculating totals (no database totals available)");
    totalNet = legalFee + ttFee + identityCheck;
    totalVAT = legalFeeVAT + ttFeeVAT + identityCheckVAT;

    if (leadData?.service_type === "Sale") {
      // Sale-specific calculations - add fees conditionally
      totalNet +=
        (isTruthy(leadData?.info?.sellingWithMortgage) ||
        isTruthy(leadData?.selling_with_mortgage)
          ? mortgageAdminFee.net
          : 0) +
        (officialCopies.net > 0 ? officialCopies.net : 0) +
        (isTruthy(leadData?.info?.auctionRepossession) ||
        isTruthy(leadData?.auction_repossession)
          ? auctionRepossessionFee.net
          : 0) +
        (isTruthy(leadData?.info?.arrangingIndemnityPolicy) ||
        isTruthy(leadData?.arranging_indemnity_policy)
          ? arrangingIndemnityPolicy.net
          : 0) +
        (leaseholdFee.net > 0 && leadData?.ownership_type === "Leasehold"
          ? leaseholdFee.net
          : 0) +
        (isTruthy(leadData?.info?.sharedOwnership) ||
        isTruthy(leadData?.shared_ownership)
          ? sharedOwnershipFee.net
          : 0);
      totalVAT +=
        (isTruthy(leadData?.info?.sellingWithMortgage) ||
        isTruthy(leadData?.selling_with_mortgage)
          ? mortgageAdminFee.vat
          : 0) +
        (officialCopies.net > 0 ? officialCopies.vat : 0) +
        (isTruthy(leadData?.info?.auctionRepossession) ||
        isTruthy(leadData?.auction_repossession)
          ? auctionRepossessionFee.vat
          : 0) +
        (isTruthy(leadData?.info?.arrangingIndemnityPolicy) ||
        isTruthy(leadData?.arranging_indemnity_policy)
          ? arrangingIndemnityPolicy.vat
          : 0) +
        (leaseholdFee.net > 0 && leadData?.ownership_type === "Leasehold"
          ? leaseholdFee.vat
          : 0) +
        (isTruthy(leadData?.info?.sharedOwnership) ||
        isTruthy(leadData?.shared_ownership)
          ? sharedOwnershipFee.vat
          : 0);
    } else {
      // Purchase-specific calculations - add fees conditionally
      totalNet +=
        (isTruthy(leadData?.info?.buyingWithMortgage) ||
        isTruthy(leadData?.buying_with_mortgage)
          ? mortgageAdminFee.net
          : 0) +
        (helpToBuyISAFee.net > 0 ? helpToBuyISAFee.net : 0) +
        (isTruthy(leadData?.info?.helpToBuy) || isTruthy(leadData?.help_to_buy)
          ? helpToBuyFee.net
          : 0) +
        (isTruthy(leadData?.info?.rightToBuy) ||
        isTruthy(leadData?.right_to_buy)
          ? rightToBuyFee.net
          : 0) +
        (isTruthy(leadData?.info?.islamicMortgage) ||
        isTruthy(leadData?.islamic_mortgage)
          ? islamicMortgageFee.net
          : 0) +
        (isTruthy(leadData?.info?.giftedDeposit) ||
        isTruthy(leadData?.gifted_deposit)
          ? giftedDepositFee
          : 0) +
        (isTruthy(leadData?.info?.buyToLet) || isTruthy(leadData?.buy_to_let)
          ? buyToLetFee.net
          : 0) +
        (isTruthy(leadData?.info?.sharedOwnership) ||
        isTruthy(leadData?.shared_ownership)
          ? sharedOwnershipFee.net
          : 0) +
        (isTruthy(leadData?.info?.newBuild) || isTruthy(leadData?.new_build)
          ? newBuildFee.net
          : 0) +
        (isTruthy(leadData?.info?.auctionRepossession) ||
        isTruthy(leadData?.auction_repossession)
          ? auctionRepossessionFee.net
          : 0) +
        (isTruthy(leadData?.info?.arrangingIndemnityPolicy) ||
        isTruthy(leadData?.arranging_indemnity_policy)
          ? arrangingIndemnityPolicy.net
          : 0) +
        (stampDuty.net > 0 ? stampDuty.net : 0) +
        hmLandRegistryFee.net +
        landRegistrySearch.net +
        landChargesSearch.net +
        searchPack.net;
      totalVAT +=
        (isTruthy(leadData?.info?.buyingWithMortgage) ||
        isTruthy(leadData?.buying_with_mortgage)
          ? mortgageAdminFee.vat
          : 0) +
        (helpToBuyISAFee.net > 0 ? helpToBuyISAFee.vat : 0) +
        (isTruthy(leadData?.info?.helpToBuy) || isTruthy(leadData?.help_to_buy)
          ? helpToBuyFee.vat
          : 0) +
        (isTruthy(leadData?.info?.rightToBuy) ||
        isTruthy(leadData?.right_to_buy)
          ? rightToBuyFee.vat
          : 0) +
        (isTruthy(leadData?.info?.islamicMortgage) ||
        isTruthy(leadData?.islamic_mortgage)
          ? islamicMortgageFee.vat
          : 0) +
        (isTruthy(leadData?.info?.giftedDeposit) ||
        isTruthy(leadData?.gifted_deposit)
          ? giftedDepositFeeVAT
          : 0) +
        (isTruthy(leadData?.info?.buyToLet) || isTruthy(leadData?.buy_to_let)
          ? buyToLetFee.vat
          : 0) +
        (isTruthy(leadData?.info?.sharedOwnership) ||
        isTruthy(leadData?.shared_ownership)
          ? sharedOwnershipFee.vat
          : 0) +
        (isTruthy(leadData?.info?.newBuild) || isTruthy(leadData?.new_build)
          ? newBuildFee.vat
          : 0) +
        (isTruthy(leadData?.info?.auctionRepossession) ||
        isTruthy(leadData?.auction_repossession)
          ? auctionRepossessionFee.vat
          : 0) +
        (isTruthy(leadData?.info?.arrangingIndemnityPolicy) ||
        isTruthy(leadData?.arranging_indemnity_policy)
          ? arrangingIndemnityPolicy.vat
          : 0) +
        stampDuty.vat +
        hmLandRegistryFee.vat +
        landRegistrySearch.vat +
        landChargesSearch.vat +
        searchPack.vat;
    }

    totalGross = totalNet + totalVAT;
  }



  // Log data when modal opens
  useEffect(() => {
    if (show) {
      console.log("📊 === VIEW QUOTE DETAIL MODAL OPENED ===");
      console.log("🔍 legalFeeData:", legalFeeData);
      console.log("📋 leadData:", leadData);

      if (legalFeeData) {
        console.log("✅ Modal opened with API data:", {
          legal_fee: legalFeeData.legal_fee,
          tt_fee: legalFeeData.tt_fee,
          identity_check: legalFeeData.identity_check,
          stamp_duty: legalFeeData.stamp_duty,
          hm_land_registry_fee: legalFeeData.hm_land_registry_fee,
          land_registry_search: legalFeeData.land_registry_search,
          land_charges_search: legalFeeData.land_charges_search,
          search_pack: legalFeeData.search_pack,
        });
      } else {
        console.log("⚠️ No legalFeeData available, using leadData fallback");
      }

      console.log("💷 Calculated values being displayed:", {
        legalFee:
          legalFee + " (should be " + (legalFeeData?.legal_fee || "N/A") + ")",
        ttFee: ttFee + " (should be " + (legalFeeData?.tt_fee || "N/A") + ")",
        identityCheck:
          identityCheck +
          " (should be " +
          (legalFeeData?.identity_check || "N/A") +
          ")",
        helpToBuyFee: helpToBuyFee + " (from all_fixed_fees)",
        landRegistrySearch:
          landRegistrySearch +
          " (should be " +
          (legalFeeData?.land_registry_search || "N/A") +
          ")",
        landChargesSearch:
          landChargesSearch +
          " (should be " +
          (legalFeeData?.land_charges_search || "N/A") +
          ")",
        stampDuty:
          stampDuty +
          " (should be " +
          (legalFeeData?.stamp_duty || "N/A") +
          ")",
        hmLandRegistryFee:
          hmLandRegistryFee +
          " (should be " +
          (legalFeeData?.hm_land_registry_fee || "N/A") +
          ")",
        searchPack:
          searchPack +
          " (should be " +
          (legalFeeData?.search_pack || "N/A") +
          ")",
      });

      // Show what's in the table
      console.log("📋 TABLE VALUES:");
      console.log("Legal Fees Net:", formatCurrency(legalFee));
      console.log("TT Fee Net:", formatCurrency(ttFee));
      console.log("Identity Check Net:", formatCurrency(identityCheck));
      console.log("Stamp Duty Net:", formatCurrency(stampDuty));
    }
  }, [show, legalFeeData, leadData]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>View Quote</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isDataLoading ? (
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-3">Loading quote details...</p>
          </div>
        ) : (
          <div ref={quoteContentRef}>
            {/* Header */}
            <div className="mb-3">
              <h5 style={{ color: "#333", marginBottom: "10px" }}>
                {leadData?.service_type || "Purchase"}
              </h5>
            </div>

            {/* 🎯 PURCHASE_SALE: Show Purchase and Sale sections separately */}
            {legalFeeData?.purchase && legalFeeData?.sale ? (
              <>
                {/* Calculate Purchase Totals if not provided */}
                {(() => {
                  // Calculate fees totals
                  let purchaseFeesNet = 0,
                    purchaseFeesVat = 0,
                    purchaseFeesGross = 0;
                  if (legalFeeData.purchase.fees) {
                    Object.values(legalFeeData.purchase.fees).forEach((fee) => {
                      if (typeof fee === "object" && fee !== null) {
                        purchaseFeesNet += Number(fee.amount || 0);
                        purchaseFeesVat += Number(fee.vat || 0);
                        purchaseFeesGross += Number(fee.gross || 0);
                      }
                    });
                  }

                  // Calculate disbursements totals
                  let purchaseDisbNet = 0,
                    purchaseDisbVat = 0,
                    purchaseDisbGross = 0;
                  if (legalFeeData.purchase.disbursements) {
                    Object.values(legalFeeData.purchase.disbursements).forEach(
                      (disb) => {
                        if (typeof disb === "object" && disb !== null) {
                          purchaseDisbNet += Number(disb.amount || 0);
                          purchaseDisbVat += Number(disb.vat || 0);
                          purchaseDisbGross += Number(disb.gross || 0);
                        }
                      },
                    );
                  }

                  // Store calculated totals
                  window.purchaseTotals = {
                    fees: {
                      net: purchaseFeesNet,
                      vat: purchaseFeesVat,
                      gross: purchaseFeesGross,
                    },
                    disb: {
                      net: purchaseDisbNet,
                      vat: purchaseDisbVat,
                      gross: purchaseDisbGross,
                    },
                    total: {
                      net: purchaseFeesNet + purchaseDisbNet,
                      vat: purchaseFeesVat + purchaseDisbVat,
                      gross: purchaseFeesGross + purchaseDisbGross,
                    },
                  };
                  return null;
                })()}

                {/* 📊 PURCHASE FEE TABLE */}
                <div className="mb-4">
                  <h6
                    className="p-2 mb-0"
                    style={{
                      backgroundColor: "#f0f0f0",
                      color: "#000000",
                      fontWeight: "600",
                    }}
                  >
                    PURCHASE
                  </h6>
                  <Table bordered className="mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: "50%" }}></th>
                        <th className="text-end" style={{ width: "16.66%" }}>
                          NET
                        </th>
                        <th className="text-end" style={{ width: "16.66%" }}>
                          VAT
                        </th>
                        <th className="text-end" style={{ width: "16.66%" }}>
                          GROSS
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Purchase Legal Cost Section */}
                      <tr style={{ backgroundColor: "#f8f9fa" }}>
                        <td colSpan="4">
                          <strong>Legal Cost</strong>
                        </td>
                      </tr>
                      {legalFeeData.purchase.fees &&
                        Object.entries(legalFeeData.purchase.fees).map(
                          ([key, value]) => {
                            if (
                              typeof value === "object" &&
                              value !== null &&
                              Number(value.amount || 0) > 0
                            ) {
                              return (
                                <tr key={key}>
                                  <td>{value.label || key}</td>
                                  <td className="text-end">
                                    £{Number(value.amount || 0).toFixed(2)}
                                  </td>
                                  <td className="text-end">
                                    £{Number(value.vat || 0).toFixed(2)}
                                  </td>
                                  <td className="text-end">
                                    £{Number(value.gross || 0).toFixed(2)}
                                  </td>
                                </tr>
                              );
                            }
                            return null;
                          },
                        )}
                      {/* Purchase Legal Cost Total */}
                      <tr
                        style={{
                          backgroundColor: "#e3f2fd",
                          fontWeight: "600",
                        }}
                      >
                        <td>TOTAL LEGAL COST</td>
                        <td className="text-end">
                          £
                          {(
                            legalFeeData.purchase.totals?.legalCost?.net ||
                            window.purchaseTotals?.fees?.net ||
                            0
                          ).toFixed(2)}
                        </td>
                        <td className="text-end">
                          £
                          {(
                            legalFeeData.purchase.totals?.legalCost?.vat ||
                            window.purchaseTotals?.fees?.vat ||
                            0
                          ).toFixed(2)}
                        </td>
                        <td className="text-end">
                          £
                          {(
                            legalFeeData.purchase.totals?.legalCost?.gross ||
                            window.purchaseTotals?.fees?.gross ||
                            0
                          ).toFixed(2)}
                        </td>
                      </tr>
                      {/* Purchase Disbursements - Always show */}
                      <tr style={{ backgroundColor: "#f8f9fa" }}>
                        <td colSpan="4">
                          <strong>
                            Disbursement - Third Party Expenditure
                          </strong>
                        </td>
                      </tr>
                      {legalFeeData.purchase.disbursements &&
                        Object.entries(legalFeeData.purchase.disbursements).map(
                          ([key, value]) => {
                            if (
                              typeof value === "object" &&
                              value !== null &&
                              Number(value.amount || 0) > 0
                            ) {
                              return (
                                <tr key={key}>
                                  <td>{value.label || key}</td>
                                  <td className="text-end">
                                    £{Number(value.amount || 0).toFixed(2)}
                                  </td>
                                  <td className="text-end">
                                    {Number(value.vat || 0) > 0
                                      ? `£${Number(value.vat).toFixed(2)}`
                                      : "-"}
                                  </td>
                                  <td className="text-end">
                                    £{Number(value.gross || 0).toFixed(2)}
                                  </td>
                                </tr>
                              );
                            }
                            return null;
                          },
                        )}
                      <tr
                        style={{
                          backgroundColor: "#e3f2fd",
                          fontWeight: "600",
                        }}
                      >
                        <td>TOTAL DISBURSEMENT COST</td>
                        <td className="text-end">
                          £
                          {(
                            legalFeeData.purchase.totals?.disbursementCost
                              ?.net ||
                            window.purchaseTotals?.disb?.net ||
                            0
                          ).toFixed(2)}
                        </td>
                        <td className="text-end">-</td>
                        <td className="text-end">
                          £
                          {(
                            legalFeeData.purchase.totals?.disbursementCost
                              ?.gross ||
                            window.purchaseTotals?.disb?.gross ||
                            0
                          ).toFixed(2)}
                        </td>
                      </tr>
                      {/* Purchase Total */}
                      <tr
                        style={{
                          backgroundColor: "#0d6efd",
                          fontWeight: "700",
                          color: "white",
                        }}
                      >
                        <td style={{ color: "white" }}>
                          Total (Legal cost + disbursement)
                        </td>
                        <td className="text-end" style={{ color: "white" }}>
                          £
                          {(
                            legalFeeData.purchase.totals?.total?.net ||
                            window.purchaseTotals?.total?.net ||
                            0
                          ).toFixed(2)}
                        </td>
                        <td className="text-end" style={{ color: "white" }}>
                          £
                          {(
                            legalFeeData.purchase.totals?.total?.vat ||
                            window.purchaseTotals?.total?.vat ||
                            0
                          ).toFixed(2)}
                        </td>
                        <td className="text-end" style={{ color: "white" }}>
                          £
                          {(
                            legalFeeData.purchase.totals?.total?.gross ||
                            window.purchaseTotals?.total?.gross ||
                            0
                          ).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>

                {/* Calculate Sale Totals if not provided */}
                {(() => {
                  // Calculate fees totals
                  let saleFeesNet = 0,
                    saleFeesVat = 0,
                    saleFeesGross = 0;
                  if (legalFeeData.sale.fees) {
                    Object.values(legalFeeData.sale.fees).forEach((fee) => {
                      if (typeof fee === "object" && fee !== null) {
                        saleFeesNet += Number(fee.amount || 0);
                        saleFeesVat += Number(fee.vat || 0);
                        saleFeesGross += Number(fee.gross || 0);
                      }
                    });
                  }

                  // Calculate disbursements totals
                  let saleDisbNet = 0,
                    saleDisbVat = 0,
                    saleDisbGross = 0;
                  if (legalFeeData.sale.disbursements) {
                    Object.values(legalFeeData.sale.disbursements).forEach(
                      (disb) => {
                        if (typeof disb === "object" && disb !== null) {
                          saleDisbNet += Number(disb.amount || 0);
                          saleDisbVat += Number(disb.vat || 0);
                          saleDisbGross += Number(disb.gross || 0);
                        }
                      },
                    );
                  }

                  // Store calculated totals
                  window.saleTotals = {
                    fees: {
                      net: saleFeesNet,
                      vat: saleFeesVat,
                      gross: saleFeesGross,
                    },
                    disb: {
                      net: saleDisbNet,
                      vat: saleDisbVat,
                      gross: saleDisbGross,
                    },
                    total: {
                      net: saleFeesNet + saleDisbNet,
                      vat: saleFeesVat + saleDisbVat,
                      gross: saleFeesGross + saleDisbGross,
                    },
                  };
                  return null;
                })()}

                {/* 📊 SALE FEE TABLE */}
                <div className="mb-4">
                  <h6
                    className="p-2 mb-0"
                    style={{
                      backgroundColor: "#f0f0f0",
                      color: "#000000",
                      fontWeight: "600",
                    }}
                  >
                    SALE
                  </h6>
                  <Table bordered className="mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: "50%" }}></th>
                        <th className="text-end" style={{ width: "16.66%" }}>
                          NET
                        </th>
                        <th className="text-end" style={{ width: "16.66%" }}>
                          VAT
                        </th>
                        <th className="text-end" style={{ width: "16.66%" }}>
                          GROSS
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Sale Legal Cost Section */}
                      <tr style={{ backgroundColor: "#f8f9fa" }}>
                        <td colSpan="4">
                          <strong>Legal Cost</strong>
                        </td>
                      </tr>
                      {legalFeeData.sale.fees &&
                        Object.entries(legalFeeData.sale.fees).map(
                          ([key, value]) => {
                            if (
                              typeof value === "object" &&
                              value !== null &&
                              Number(value.amount || 0) > 0
                            ) {
                              return (
                                <tr key={key}>
                                  <td>{value.label || key}</td>
                                  <td className="text-end">
                                    £{Number(value.amount || 0).toFixed(2)}
                                  </td>
                                  <td className="text-end">
                                    £{Number(value.vat || 0).toFixed(2)}
                                  </td>
                                  <td className="text-end">
                                    £{Number(value.gross || 0).toFixed(2)}
                                  </td>
                                </tr>
                              );
                            }
                            return null;
                          },
                        )}
                      {/* Sale Legal Cost Total */}
                      <tr
                        style={{
                          backgroundColor: "#f8d7da",
                          fontWeight: "600",
                        }}
                      >
                        <td>TOTAL LEGAL COST</td>
                        <td className="text-end">
                          £
                          {(
                            legalFeeData.sale.totals?.legalCost?.net ||
                            window.saleTotals?.fees?.net ||
                            0
                          ).toFixed(2)}
                        </td>
                        <td className="text-end">
                          £
                          {(
                            legalFeeData.sale.totals?.legalCost?.vat ||
                            window.saleTotals?.fees?.vat ||
                            0
                          ).toFixed(2)}
                        </td>
                        <td className="text-end">
                          £
                          {(
                            legalFeeData.sale.totals?.legalCost?.gross ||
                            window.saleTotals?.fees?.gross ||
                            0
                          ).toFixed(2)}
                        </td>
                      </tr>
                      {/* Sale Disbursements - Always show */}
                      <tr style={{ backgroundColor: "#f8f9fa" }}>
                        <td colSpan="4">
                          <strong>
                            Disbursement - Third Party Expenditure
                          </strong>
                        </td>
                      </tr>
                      {legalFeeData.sale.disbursements &&
                        Object.entries(legalFeeData.sale.disbursements).map(
                          ([key, value]) => {
                            if (
                              typeof value === "object" &&
                              value !== null &&
                              Number(value.amount || 0) > 0
                            ) {
                              return (
                                <tr key={key}>
                                  <td>{value.label || key}</td>
                                  <td className="text-end">
                                    £{Number(value.amount || 0).toFixed(2)}
                                  </td>
                                  <td className="text-end">
                                    {Number(value.vat || 0) > 0
                                      ? `£${Number(value.vat).toFixed(2)}`
                                      : "-"}
                                  </td>
                                  <td className="text-end">
                                    £{Number(value.gross || 0).toFixed(2)}
                                  </td>
                                </tr>
                              );
                            }
                            return null;
                          },
                        )}
                      <tr
                        style={{
                          backgroundColor: "#f8d7da",
                          fontWeight: "600",
                        }}
                      >
                        <td>TOTAL DISBURSEMENT COST</td>
                        <td className="text-end">
                          £
                          {(
                            legalFeeData.sale.totals?.disbursementCost?.net ||
                            window.saleTotals?.disb?.net ||
                            0
                          ).toFixed(2)}
                        </td>
                        <td className="text-end">-</td>
                        <td className="text-end">
                          £
                          {(
                            legalFeeData.sale.totals?.disbursementCost?.gross ||
                            window.saleTotals?.disb?.gross ||
                            0
                          ).toFixed(2)}
                        </td>
                      </tr>
                      {/* Sale Total */}
                      <tr
                        style={{
                          backgroundColor: "#dc3545",
                          fontWeight: "700",
                          color: "white",
                        }}
                      >
                        <td style={{ color: "white" }}>
                          Total (Legal cost + disbursement)
                        </td>
                        <td className="text-end" style={{ color: "white" }}>
                          £
                          {(
                            legalFeeData.sale.totals?.total?.net ||
                            window.saleTotals?.total?.net ||
                            0
                          ).toFixed(2)}
                        </td>
                        <td className="text-end" style={{ color: "white" }}>
                          £
                          {(
                            legalFeeData.sale.totals?.total?.vat ||
                            window.saleTotals?.total?.vat ||
                            0
                          ).toFixed(2)}
                        </td>
                        <td className="text-end" style={{ color: "white" }}>
                          £
                          {(
                            legalFeeData.sale.totals?.total?.gross ||
                            window.saleTotals?.total?.gross ||
                            0
                          ).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>

                {/* COMBINED TOTAL removed as per user request */}
              </>
            ) : (
              /* Normal single table for Purchase or Sale only */
              <>
                {/* Quote Table */}
                <Table bordered>
                  <thead>
                    <tr style={{ backgroundColor: "#f8f9fa" }}>
                      <th style={{ width: "50%" }}></th>
                      <th className="text-end" style={{ width: "16.66%" }}>
                        Net
                      </th>
                      <th className="text-end" style={{ width: "16.66%" }}>
                        VAT
                      </th>
                      <th className="text-end" style={{ width: "16.66%" }}>
                        Gross
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Legal Cost Section */}
                    <tr style={{ backgroundColor: "#f8f9fa" }}>
                      <td colSpan="4">
                        <strong>Legal Cost</strong>
                      </td>
                    </tr>
                    {/* Base Fees */}
                    <tr>
                      <td>
                        {leadData?.service_type === "Grant_Application" ||
                        leadData?.service_type === "Grant Application"
                          ? "MLM Legal Cost"
                          : "Legal Fees"}
                      </td>
                      <td className="text-end">{formatCurrency(legalFee)}</td>
                      <td className="text-end">
                        {formatCurrency(legalFeeVAT)}
                      </td>
                      <td className="text-end">
                        {formatCurrency(legalFeeGross)}
                      </td>
                    </tr>

                    {/* Service-specific fixed fees */}
                    {(() => {
                      const serviceType =
                        leadData?.service_type ||
                        legalFeeData?.service_type ||
                        "";
                      const isGrantApplication =
                        serviceType === "Grant_Application" ||
                        serviceType === "Grant Application";
                      const isFirstRegistration =
                        serviceType === "First_Registration" ||
                        serviceType === "First Registration";

                      // Grant Application: ONLY Legal Fees (no TT, no Identity Check)
                      if (isGrantApplication) {
                        return null;
                      }

                      // First Registration: ONLY Identity Check (NO TT Fee)
                      if (isFirstRegistration) {
                        return (
                          <tr>
                            <td>Identity Check</td>
                            <td className="text-end">
                              {formatCurrency(identityCheck)}
                            </td>
                            <td className="text-end">
                              {formatCurrency(identityCheckVAT)}
                            </td>
                            <td className="text-end">
                              {formatCurrency(identityCheckGross)}
                            </td>
                          </tr>
                        );
                      }

                      // All other services: Show both TT Fee and Identity Check
                      return (
                        <>
                          <tr>
                            <td>Telegraphic Transfer (TT) Fee</td>
                            <td className="text-end">
                              {formatCurrency(ttFee)}
                            </td>
                            <td className="text-end">
                              {formatCurrency(ttFeeVAT)}
                            </td>
                            <td className="text-end">
                              {formatCurrency(ttFeeGross)}
                            </td>
                          </tr>
                          <tr>
                            <td>Identity Check</td>
                            <td className="text-end">
                              {formatCurrency(identityCheck)}
                            </td>
                            <td className="text-end">
                              {formatCurrency(identityCheckVAT)}
                            </td>
                            <td className="text-end">
                              {formatCurrency(identityCheckGross)}
                            </td>
                          </tr>
                        </>
                      );
                    })()}

                    {/* HM Land Registry Fee - Show in Legal Cost for Transfer of Equity */}
                    {(leadData?.service_type === "Transfer_of_Equity" ||
                      leadData?.service_type === "Transfer of Equity") &&
                      hmLandRegistryFee.net > 0 && (
                        <tr>
                          <td>H M Land Registry Fee</td>
                          <td className="text-end">
                            {formatCurrency(hmLandRegistryFee.net)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(hmLandRegistryFee.vat)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(hmLandRegistryFee.gross)}
                          </td>
                        </tr>
                      )}

                    {/* Additional Fixed Fees - Only show if info field is true AND NOT Grant Application */}
                    {leadData?.service_type !== "Grant_Application" &&
                      leadData?.service_type !== "Grant Application" &&
                      ((leadData?.service_type === "Sale" &&
                        (isTruthy(leadData?.info?.sellingWithMortgage) ||
                          isTruthy(leadData?.selling_with_mortgage))) ||
                        (leadData?.service_type !== "Sale" &&
                          (isTruthy(leadData?.info?.buyingWithMortgage) ||
                            isTruthy(leadData?.buying_with_mortgage)))) && (
                        <tr>
                          <td>Mortgage Admin Fee</td>
                          <td className="text-end">
                            {formatCurrency(mortgageAdminFee.net)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(mortgageAdminFee.vat)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(mortgageAdminFee.gross)}
                          </td>
                        </tr>
                      )}
                    {leadData?.service_type !== "Grant_Application" &&
                      leadData?.service_type !== "Grant Application" &&
                      leaseholdFee.net > 0 &&
                      leadData?.ownership_type === "Leasehold" && (
                        <tr>
                          <td>Leasehold Fee</td>
                          <td className="text-end">
                            {formatCurrency(leaseholdFee.net)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(leaseholdFee.vat)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(leaseholdFee.gross)}
                          </td>
                        </tr>
                      )}
                    {leadData?.service_type !== "Grant_Application" &&
                      leadData?.service_type !== "Grant Application" &&
                      helpToBuyISAFee.net > 0 && (
                        <tr>
                          <td>Help to Buy ISA Fee</td>
                          <td className="text-end">
                            {formatCurrency(helpToBuyISAFee.net)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(helpToBuyISAFee.vat)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(helpToBuyISAFee.gross)}
                          </td>
                        </tr>
                      )}
                    {leadData?.service_type !== "Grant_Application" &&
                      leadData?.service_type !== "Grant Application" &&
                      (isTruthy(leadData?.info?.helpToBuy) ||
                        isTruthy(leadData?.help_to_buy)) && (
                        <tr>
                          <td>Help to Buy Fee</td>
                          <td className="text-end">
                            {formatCurrency(helpToBuyFee.net)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(helpToBuyFee.vat)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(helpToBuyFee.gross)}
                          </td>
                        </tr>
                      )}
                    {leadData?.service_type !== "Grant_Application" &&
                      leadData?.service_type !== "Grant Application" &&
                      (isTruthy(leadData?.info?.rightToBuy) ||
                        isTruthy(leadData?.right_to_buy)) && (
                        <tr>
                          <td>Right to Buy Fee</td>
                          <td className="text-end">
                            {formatCurrency(rightToBuyFee.net)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(rightToBuyFee.vat)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(rightToBuyFee.gross)}
                          </td>
                        </tr>
                      )}
                    {/* ❌ REMOVED: Help to Buy Equity Loan Fee */}
                    {/* ❌ REMOVED: First Time Buyer Fee - not a separate fee, first-time buyer affects SDLT only */}
                    {leadData?.service_type !== "Grant_Application" &&
                      leadData?.service_type !== "Grant Application" &&
                      (isTruthy(leadData?.info?.islamicMortgage) ||
                        isTruthy(leadData?.islamic_mortgage)) && (
                        <tr>
                          <td>Islamic Mortgage Fee</td>
                          <td className="text-end">
                            {formatCurrency(islamicMortgageFee.net)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(islamicMortgageFee.vat)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(islamicMortgageFee.gross)}
                          </td>
                        </tr>
                      )}
                    {leadData?.service_type !== "Grant_Application" &&
                      leadData?.service_type !== "Grant Application" &&
                      (isTruthy(leadData?.info?.giftedDeposit) ||
                        isTruthy(leadData?.gifted_deposit)) && (
                        <tr>
                          <td>Gifted Deposit Fee (First Giftor)</td>
                          <td className="text-end">
                            {formatCurrency(giftedDepositFeeFirstGiftor.net)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(giftedDepositFeeFirstGiftor.vat)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(giftedDepositFeeFirstGiftor.gross)}
                          </td>
                        </tr>
                      )}
                    {leadData?.service_type !== "Grant_Application" &&
                      leadData?.service_type !== "Grant Application" &&
                      (isTruthy(leadData?.info?.buyToLet) ||
                        isTruthy(leadData?.buy_to_let)) && (
                        <tr>
                          <td>Buy to Let Fee</td>
                          <td className="text-end">
                            {formatCurrency(buyToLetFee.net)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(buyToLetFee.vat)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(buyToLetFee.gross)}
                          </td>
                        </tr>
                      )}
                    {leadData?.service_type !== "Grant_Application" &&
                      leadData?.service_type !== "Grant Application" &&
                      (isTruthy(leadData?.info?.sharedOwnership) ||
                        isTruthy(leadData?.shared_ownership)) && (
                        <tr>
                          <td>Shared Ownership Fee</td>
                          <td className="text-end">
                            {formatCurrency(sharedOwnershipFee.net)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(sharedOwnershipFee.vat)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(sharedOwnershipFee.gross)}
                          </td>
                        </tr>
                      )}
                    {leadData?.service_type !== "Grant_Application" &&
                      leadData?.service_type !== "Grant Application" &&
                      (isTruthy(leadData?.info?.newBuild) ||
                        isTruthy(leadData?.new_build)) && (
                        <tr>
                          <td>New Build Fee</td>
                          <td className="text-end">
                            {formatCurrency(newBuildFee.net)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(newBuildFee.vat)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(newBuildFee.gross)}
                          </td>
                        </tr>
                      )}
                    {leadData?.service_type !== "Grant_Application" &&
                      leadData?.service_type !== "Grant Application" &&
                      (isTruthy(leadData?.info?.auctionRepossession) ||
                        isTruthy(leadData?.auction_repossession)) && (
                        <tr>
                          <td>Auction/Repossession Fee</td>
                          <td className="text-end">
                            {formatCurrency(auctionRepossessionFee.net)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(auctionRepossessionFee.vat)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(auctionRepossessionFee.gross)}
                          </td>
                        </tr>
                      )}
                    {leadData?.service_type !== "Grant_Application" &&
                      leadData?.service_type !== "Grant Application" &&
                      (isTruthy(leadData?.info?.arrangingIndemnityPolicy) ||
                        isTruthy(leadData?.arranging_indemnity_policy)) && (
                        <tr>
                          <td>Arranging Indemnity Policy</td>
                          <td className="text-end">
                            {formatCurrency(arrangingIndemnityPolicy.net)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(arrangingIndemnityPolicy.vat)}
                          </td>
                          <td className="text-end">
                            {formatCurrency(arrangingIndemnityPolicy.gross)}
                          </td>
                        </tr>
                      )}

                    {/* Calculate Total Legal Cost */}
                    <tr
                      style={{ backgroundColor: "#e3f2fd", fontWeight: "600" }}
                    >
                      <td>TOTAL LEGAL COST</td>
                      <td className="text-end">
                        {formatCurrency(
                          calculationTotals?.legalCost?.net ||
                            (leadData?.service_type === "Sale"
                              ? legalFee +
                                ttFee +
                                identityCheck +
                                (isTruthy(
                                  leadData?.info?.sellingWithMortgage,
                                ) || isTruthy(leadData?.selling_with_mortgage)
                                  ? mortgageAdminFee.net
                                  : 0) +
                                (leaseholdFee.net > 0 &&
                                leadData?.ownership_type === "Leasehold"
                                  ? leaseholdFee.net
                                  : 0) +
                                (isTruthy(leadData?.info?.sharedOwnership) ||
                                isTruthy(leadData?.shared_ownership)
                                  ? sharedOwnershipFee.net
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.auctionRepossession,
                                ) || isTruthy(leadData?.auction_repossession)
                                  ? auctionRepossessionFee.net
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.arrangingIndemnityPolicy,
                                ) ||
                                isTruthy(leadData?.arranging_indemnity_policy)
                                  ? arrangingIndemnityPolicy.net
                                  : 0)
                              : legalFee +
                                ttFee +
                                identityCheck +
                                (isTruthy(leadData?.info?.buyingWithMortgage) ||
                                isTruthy(leadData?.buying_with_mortgage)
                                  ? mortgageAdminFee.net
                                  : 0) +
                                (leaseholdFee.net > 0 &&
                                leadData?.ownership_type === "Leasehold"
                                  ? leaseholdFee.net
                                  : 0) +
                                (helpToBuyISAFee.net > 0
                                  ? helpToBuyISAFee.net
                                  : 0) +
                                (isTruthy(leadData?.info?.helpToBuy) ||
                                isTruthy(leadData?.help_to_buy)
                                  ? helpToBuyFee.net
                                  : 0) +
                                (isTruthy(leadData?.info?.rightToBuy) ||
                                isTruthy(leadData?.right_to_buy)
                                  ? rightToBuyFee.net
                                  : 0) +
                                (isTruthy(leadData?.info?.islamicMortgage) ||
                                isTruthy(leadData?.islamic_mortgage)
                                  ? islamicMortgageFee.net
                                  : 0) +
                                (isTruthy(leadData?.info?.giftedDeposit) ||
                                isTruthy(leadData?.gifted_deposit)
                                  ? giftedDepositFeeFirstGiftor.net
                                  : 0) +
                                (isTruthy(leadData?.info?.buyToLet) ||
                                isTruthy(leadData?.buy_to_let)
                                  ? buyToLetFee.net
                                  : 0) +
                                (isTruthy(leadData?.info?.sharedOwnership) ||
                                isTruthy(leadData?.shared_ownership)
                                  ? sharedOwnershipFee.net
                                  : 0) +
                                (isTruthy(leadData?.info?.newBuild) ||
                                isTruthy(leadData?.new_build)
                                  ? newBuildFee.net
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.auctionRepossession,
                                ) || isTruthy(leadData?.auction_repossession)
                                  ? auctionRepossessionFee.net
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.arrangingIndemnityPolicy,
                                ) ||
                                isTruthy(leadData?.arranging_indemnity_policy)
                                  ? arrangingIndemnityPolicy.net
                                  : 0)),
                        )}
                      </td>
                      <td className="text-end">
                        {formatCurrency(
                          calculationTotals?.legalCost?.vat ||
                            (leadData?.service_type === "Sale"
                              ? legalFeeVAT +
                                ttFeeVAT +
                                identityCheckVAT +
                                (isTruthy(
                                  leadData?.info?.sellingWithMortgage,
                                ) || isTruthy(leadData?.selling_with_mortgage)
                                  ? mortgageAdminFee.vat
                                  : 0) +
                                (leaseholdFee.net > 0 &&
                                leadData?.ownership_type === "Leasehold"
                                  ? leaseholdFee.vat
                                  : 0) +
                                (isTruthy(leadData?.info?.sharedOwnership) ||
                                isTruthy(leadData?.shared_ownership)
                                  ? sharedOwnershipFee.vat
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.auctionRepossession,
                                ) || isTruthy(leadData?.auction_repossession)
                                  ? auctionRepossessionFee.vat
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.arrangingIndemnityPolicy,
                                ) ||
                                isTruthy(leadData?.arranging_indemnity_policy)
                                  ? arrangingIndemnityPolicy.vat
                                  : 0)
                              : legalFeeVAT +
                                ttFeeVAT +
                                identityCheckVAT +
                                (isTruthy(leadData?.info?.buyingWithMortgage) ||
                                isTruthy(leadData?.buying_with_mortgage)
                                  ? mortgageAdminFee.vat
                                  : 0) +
                                (leaseholdFee.net > 0 &&
                                leadData?.ownership_type === "Leasehold"
                                  ? leaseholdFee.vat
                                  : 0) +
                                (helpToBuyISAFee.net > 0
                                  ? helpToBuyISAFee.vat
                                  : 0) +
                                (isTruthy(leadData?.info?.helpToBuy) ||
                                isTruthy(leadData?.help_to_buy)
                                  ? helpToBuyFee.vat
                                  : 0) +
                                (isTruthy(leadData?.info?.rightToBuy) ||
                                isTruthy(leadData?.right_to_buy)
                                  ? rightToBuyFee.vat
                                  : 0) +
                                (isTruthy(leadData?.info?.islamicMortgage) ||
                                isTruthy(leadData?.islamic_mortgage)
                                  ? islamicMortgageFee.vat
                                  : 0) +
                                (isTruthy(leadData?.info?.giftedDeposit) ||
                                isTruthy(leadData?.gifted_deposit)
                                  ? giftedDepositFeeFirstGiftor.vat
                                  : 0) +
                                (isTruthy(leadData?.info?.buyToLet) ||
                                isTruthy(leadData?.buy_to_let)
                                  ? buyToLetFee.vat
                                  : 0) +
                                (isTruthy(leadData?.info?.sharedOwnership) ||
                                isTruthy(leadData?.shared_ownership)
                                  ? sharedOwnershipFee.vat
                                  : 0) +
                                (isTruthy(leadData?.info?.newBuild) ||
                                isTruthy(leadData?.new_build)
                                  ? newBuildFee.vat
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.auctionRepossession,
                                ) || isTruthy(leadData?.auction_repossession)
                                  ? auctionRepossessionFee.vat
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.arrangingIndemnityPolicy,
                                ) ||
                                isTruthy(leadData?.arranging_indemnity_policy)
                                  ? arrangingIndemnityPolicy.vat
                                  : 0)),
                        )}
                      </td>
                      <td className="text-end">
                        {formatCurrency(
                          calculationTotals?.legalCost?.gross ||
                            (leadData?.service_type === "Sale"
                              ? legalFeeGross +
                                ttFeeGross +
                                identityCheckGross +
                                (isTruthy(
                                  leadData?.info?.sellingWithMortgage,
                                ) || isTruthy(leadData?.selling_with_mortgage)
                                  ? mortgageAdminFee.gross
                                  : 0) +
                                (leaseholdFee.net > 0 &&
                                leadData?.ownership_type === "Leasehold"
                                  ? leaseholdFee.gross
                                  : 0) +
                                (isTruthy(leadData?.info?.sharedOwnership) ||
                                isTruthy(leadData?.shared_ownership)
                                  ? sharedOwnershipFee.gross
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.auctionRepossession,
                                ) || isTruthy(leadData?.auction_repossession)
                                  ? auctionRepossessionFee.gross
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.arrangingIndemnityPolicy,
                                ) ||
                                isTruthy(leadData?.arranging_indemnity_policy)
                                  ? arrangingIndemnityPolicy.gross
                                  : 0)
                              : legalFeeGross +
                                ttFeeGross +
                                identityCheckGross +
                                (isTruthy(leadData?.info?.buyingWithMortgage) ||
                                isTruthy(leadData?.buying_with_mortgage)
                                  ? mortgageAdminFee.gross
                                  : 0) +
                                (leaseholdFee.net > 0 &&
                                leadData?.ownership_type === "Leasehold"
                                  ? leaseholdFee.gross
                                  : 0) +
                                (helpToBuyISAFee.net > 0
                                  ? helpToBuyISAFee.gross
                                  : 0) +
                                (isTruthy(leadData?.info?.helpToBuy) ||
                                isTruthy(leadData?.help_to_buy)
                                  ? helpToBuyFee.gross
                                  : 0) +
                                (isTruthy(leadData?.info?.rightToBuy) ||
                                isTruthy(leadData?.right_to_buy)
                                  ? rightToBuyFee.gross
                                  : 0) +
                                (isTruthy(leadData?.info?.islamicMortgage) ||
                                isTruthy(leadData?.islamic_mortgage)
                                  ? islamicMortgageFee.gross
                                  : 0) +
                                (isTruthy(leadData?.info?.giftedDeposit) ||
                                isTruthy(leadData?.gifted_deposit)
                                  ? giftedDepositFeeFirstGiftor.gross
                                  : 0) +
                                (isTruthy(leadData?.info?.buyToLet) ||
                                isTruthy(leadData?.buy_to_let)
                                  ? buyToLetFee.gross
                                  : 0) +
                                (isTruthy(leadData?.info?.sharedOwnership) ||
                                isTruthy(leadData?.shared_ownership)
                                  ? sharedOwnershipFee.gross
                                  : 0) +
                                (isTruthy(leadData?.info?.newBuild) ||
                                isTruthy(leadData?.new_build)
                                  ? newBuildFee.gross
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.auctionRepossession,
                                ) || isTruthy(leadData?.auction_repossession)
                                  ? auctionRepossessionFee.gross
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.arrangingIndemnityPolicy,
                                ) ||
                                isTruthy(leadData?.arranging_indemnity_policy)
                                  ? arrangingIndemnityPolicy.gross
                                  : 0)),
                        )}
                      </td>
                    </tr>

                    {/* Disbursement Section - Hide for Grant Application */}
                    {leadData?.service_type !== "Grant_Application" &&
                      leadData?.service_type !== "Grant Application" && (
                        <>
                          <tr style={{ backgroundColor: "#f8f9fa" }}>
                            <td colSpan="4">
                              <strong>
                                Disbursement - Third Party Expenditure
                              </strong>
                            </td>
                          </tr>

                          {/* 🎯 SERVICE-TYPE AWARE DISBURSEMENTS */}
                          {(() => {
                            const serviceType =
                              leadData?.service_type ||
                              legalFeeData?.service_type ||
                              "";
                            const isMortgageRedemption =
                              serviceType === "Mortgage_Redemption" ||
                              serviceType === "Mortgage Redemption";
                            const isRemortgage = serviceType === "Remortgage";
                            const isTransferOfEquity =
                              serviceType === "Transfer_of_Equity" ||
                              serviceType === "Transfer of Equity";
                            const isFirstRegistration =
                              serviceType === "First_Registration" ||
                              serviceType === "First Registration";
                            const isReviewOfLegalPack =
                              serviceType === "Review_Legal_Pack" ||
                              serviceType === "Review of Legal Pack";
                            const isPurchase = serviceType === "Purchase";
                            const isSale = serviceType === "Sale";

                            // For Remortgage: Show HM Land Registry, searches, and Official Copies (no Stamp Duty or Search Pack)
                            if (isRemortgage) {
                              return (
                                <>
                                  <tr>
                                    <td>HM Land Registry Fee</td>
                                    <td className="text-end">
                                      {formatCurrency(hmLandRegistryFee.net)}
                                    </td>
                                    <td className="text-end">-</td>
                                    <td className="text-end">
                                      {formatCurrency(hmLandRegistryFee.gross)}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Land Registry Search</td>
                                    <td className="text-end">
                                      {formatCurrency(landRegistrySearch.net)}
                                    </td>
                                    <td className="text-end">
                                      {formatCurrency(landRegistrySearch.vat)}
                                    </td>
                                    <td className="text-end">
                                      {formatCurrency(landRegistrySearch.gross)}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Land Charges Search</td>
                                    <td className="text-end">
                                      {formatCurrency(landChargesSearch.net)}
                                    </td>
                                    <td className="text-end">
                                      {formatCurrency(landChargesSearch.vat)}
                                    </td>
                                    <td className="text-end">
                                      {formatCurrency(landChargesSearch.gross)}
                                    </td>
                                  </tr>
                                  {officialCopies.net > 0 && (
                                    <tr>
                                      <td>Official Copies</td>
                                      <td className="text-end">
                                        {formatCurrency(officialCopies.net)}
                                      </td>
                                      <td className="text-end">
                                        {officialCopies.vat > 0
                                          ? formatCurrency(officialCopies.vat)
                                          : "-"}
                                      </td>
                                      <td className="text-end">
                                        {formatCurrency(officialCopies.gross)}
                                      </td>
                                    </tr>
                                  )}
                                </>
                              );
                            }

                            // For Transfer of Equity: Show ALL disbursements dynamically
                            // NOTE: HM Land Registry Fee is shown in Legal Cost section, not here
                            if (isTransferOfEquity) {
                              return (
                                <>
                                  {(() => {
                                    const localSearchInsurance = getFeeWithVAT(
                                      "localSearchInsurance",
                                      false,
                                    );
                                    if (localSearchInsurance.net > 0) {
                                      return (
                                        <tr>
                                          <td>Local Search Insurance</td>
                                          <td className="text-end">
                                            {formatCurrency(
                                              localSearchInsurance.net,
                                            )}
                                          </td>
                                          <td className="text-end">-</td>
                                          <td className="text-end">
                                            {formatCurrency(
                                              localSearchInsurance.gross,
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    }
                                  })()}
                                  {officialCopies.net > 0 && (
                                    <tr>
                                      <td>Official Copies of Title</td>
                                      <td className="text-end">
                                        {formatCurrency(officialCopies.net)}
                                      </td>
                                      <td className="text-end">
                                        {officialCopies.vat > 0
                                          ? formatCurrency(officialCopies.vat)
                                          : "-"}
                                      </td>
                                      <td className="text-end">
                                        {formatCurrency(officialCopies.gross)}
                                      </td>
                                    </tr>
                                  )}
                                  {landRegistrySearch.net > 0 && (
                                    <tr>
                                      <td>Land Registry Search</td>
                                      <td className="text-end">
                                        {formatCurrency(landRegistrySearch.net)}
                                      </td>
                                      <td className="text-end">
                                        {formatCurrency(landRegistrySearch.vat)}
                                      </td>
                                      <td className="text-end">
                                        {formatCurrency(
                                          landRegistrySearch.gross,
                                        )}
                                      </td>
                                    </tr>
                                  )}
                                  {landChargesSearch.net > 0 && (
                                    <tr>
                                      <td>Land Charges Search</td>
                                      <td className="text-end">
                                        {formatCurrency(landChargesSearch.net)}
                                      </td>
                                      <td className="text-end">
                                        {formatCurrency(landChargesSearch.vat)}
                                      </td>
                                      <td className="text-end">
                                        {formatCurrency(
                                          landChargesSearch.gross,
                                        )}
                                      </td>
                                    </tr>
                                  )}
                                  {stampDuty.net > 0 && (
                                    <tr>
                                      <td>Stamp Duty Land Tax (SDLT)</td>
                                      <td className="text-end">
                                        {formatCurrency(stampDuty.net)}
                                      </td>
                                      <td className="text-end">-</td>
                                      <td className="text-end">
                                        {formatCurrency(stampDuty.gross)}
                                      </td>
                                    </tr>
                                  )}
                                </>
                              );
                            }

                            // For First Registration: Show HM Land Registry Fee and Land Registry Search
                            if (isFirstRegistration) {
                              return (
                                <>
                                  <tr>
                                    <td>HM Land Registry Fee</td>
                                    <td className="text-end">
                                      {formatCurrency(hmLandRegistryFee.net)}
                                    </td>
                                    <td className="text-end">-</td>
                                    <td className="text-end">
                                      {formatCurrency(hmLandRegistryFee.gross)}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Land Registry Search</td>
                                    <td className="text-end">
                                      {formatCurrency(landRegistrySearch.net)}
                                    </td>
                                    <td className="text-end">-</td>
                                    <td className="text-end">
                                      {formatCurrency(landRegistrySearch.net)}
                                    </td>
                                  </tr>
                                </>
                              );
                            }

                            // For Mortgage Redemption, Review of Legal Pack:
                            // ONLY show Official Copies (no Stamp Duty, HM Land Registry, searches, etc.)
                            if (isMortgageRedemption || isReviewOfLegalPack) {
                              return (
                                <>
                                  {officialCopies.net > 0 && (
                                    <tr>
                                      <td>Official Copies</td>
                                      <td className="text-end">
                                        {formatCurrency(officialCopies.net)}
                                      </td>
                                      <td className="text-end">
                                        {officialCopies.vat > 0
                                          ? formatCurrency(officialCopies.vat)
                                          : "-"}
                                      </td>
                                      <td className="text-end">
                                        {formatCurrency(officialCopies.gross)}
                                      </td>
                                    </tr>
                                  )}
                                </>
                              );
                            }

                            // For Purchase/Sale: Show all disbursements
                            return (
                              <>
                                {stampDuty.net > 0 && (
                                  <tr>
                                    <td>Stamp Duty</td>
                                    <td className="text-end">
                                      {formatCurrency(stampDuty.net)}
                                    </td>
                                    <td className="text-end">-</td>
                                    <td className="text-end">
                                      {formatCurrency(stampDuty.gross)}
                                    </td>
                                  </tr>
                                )}
                                <tr>
                                  <td>HM Land Registry Fee</td>
                                  <td className="text-end">
                                    {formatCurrency(hmLandRegistryFee.net)}
                                  </td>
                                  <td className="text-end">-</td>
                                  <td className="text-end">
                                    {formatCurrency(hmLandRegistryFee.gross)}
                                  </td>
                                </tr>
                                <tr>
                                  <td>Land Registry Search</td>
                                  <td className="text-end">
                                    {formatCurrency(landRegistrySearch.net)}
                                  </td>
                                  <td className="text-end">
                                    {formatCurrency(landRegistrySearch.vat)}
                                  </td>
                                  <td className="text-end">
                                    {formatCurrency(landRegistrySearch.gross)}
                                  </td>
                                </tr>
                                <tr>
                                  <td>Land Charges Search</td>
                                  <td className="text-end">
                                    {formatCurrency(landChargesSearch.net)}
                                  </td>
                                  <td className="text-end">
                                    {formatCurrency(landChargesSearch.vat)}
                                  </td>
                                  <td className="text-end">
                                    {formatCurrency(landChargesSearch.gross)}
                                  </td>
                                </tr>
                                <tr>
                                  <td>Search Pack</td>
                                  <td className="text-end">
                                    {formatCurrency(searchPack.net)}
                                  </td>
                                  <td className="text-end">-</td>
                                  <td className="text-end">
                                    {formatCurrency(searchPack.gross)}
                                  </td>
                                </tr>
                                {officialCopies.net > 0 && (
                                  <tr>
                                    <td>Official Copies</td>
                                    <td className="text-end">
                                      {formatCurrency(officialCopies.net)}
                                    </td>
                                    <td className="text-end">
                                      {officialCopies.vat > 0
                                        ? formatCurrency(officialCopies.vat)
                                        : "-"}
                                    </td>
                                    <td className="text-end">
                                      {formatCurrency(officialCopies.gross)}
                                    </td>
                                  </tr>
                                )}
                              </>
                            );
                          })()}
                          <tr
                            style={{
                              backgroundColor: "#e3f2fd",
                              fontWeight: "600",
                            }}
                          >
                            <td>TOTAL DISBURSEMENT COST</td>
                            <td className="text-end">
                              {formatCurrency(
                                calculationTotals?.disbursementCost?.net ||
                                  (() => {
                                    const serviceType =
                                      leadData?.service_type ||
                                      legalFeeData?.service_type ||
                                      "";
                                    const isMortgageRedemption =
                                      serviceType === "Mortgage_Redemption" ||
                                      serviceType === "Mortgage Redemption";
                                    const isRemortgage =
                                      serviceType === "Remortgage";
                                    const isTransferOfEquity =
                                      serviceType === "Transfer_of_Equity" ||
                                      serviceType === "Transfer of Equity";
                                    const isFirstRegistration =
                                      serviceType === "First_Registration" ||
                                      serviceType === "First Registration";
                                    const isReviewOfLegalPack =
                                      serviceType === "Review_Legal_Pack" ||
                                      serviceType === "Review of Legal Pack";
                                    const isSale = serviceType === "Sale";

                                    if (isRemortgage) {
                                      return (
                                        hmLandRegistryFee.net +
                                        landRegistrySearch.net +
                                        landChargesSearch.net +
                                        (officialCopies.net > 0
                                          ? officialCopies.net
                                          : 0)
                                      );
                                    } else if (isTransferOfEquity) {
                                      // Transfer of Equity: Include all disbursements that may exist
                                      const localSearchIns = getFeeWithVAT(
                                        "localSearchInsurance",
                                        false,
                                      );
                                      return (
                                        (stampDuty.net > 0
                                          ? stampDuty.net
                                          : 0) +
                                        (searchPack.net > 0
                                          ? searchPack.net
                                          : 0) +
                                        (localSearchIns.net > 0
                                          ? localSearchIns.net
                                          : 0) +
                                        (landRegistrySearch.net > 0
                                          ? landRegistrySearch.net
                                          : 0) +
                                        (landChargesSearch.net > 0
                                          ? landChargesSearch.net
                                          : 0) +
                                        (officialCopies.net > 0
                                          ? officialCopies.net
                                          : 0)
                                      );
                                    } else if (isFirstRegistration) {
                                      // First Registration: HM Land Registry Fee + Land Registry Search
                                      return (
                                        hmLandRegistryFee.net +
                                        landRegistrySearch.net
                                      );
                                    } else if (
                                      isMortgageRedemption ||
                                      isReviewOfLegalPack
                                    ) {
                                      return officialCopies.net > 0
                                        ? officialCopies.net
                                        : 0;
                                    } else if (isSale) {
                                      return officialCopies.net > 0
                                        ? officialCopies.net
                                        : 0;
                                    } else {
                                      return (
                                        (stampDuty.net > 0
                                          ? stampDuty.net
                                          : 0) +
                                        hmLandRegistryFee.net +
                                        landRegistrySearch.net +
                                        landChargesSearch.net +
                                        searchPack.net +
                                        (officialCopies.net > 0
                                          ? officialCopies.net
                                          : 0)
                                      );
                                    }
                                  })(),
                              )}
                            </td>
                            <td className="text-end">
                              {formatCurrency(
                                calculationTotals?.disbursementCost?.vat ||
                                  (() => {
                                    const serviceType =
                                      leadData?.service_type ||
                                      legalFeeData?.service_type ||
                                      "";
                                    const isMortgageRedemption =
                                      serviceType === "Mortgage_Redemption" ||
                                      serviceType === "Mortgage Redemption";
                                    const isRemortgage =
                                      serviceType === "Remortgage";
                                    const isTransferOfEquity =
                                      serviceType === "Transfer_of_Equity" ||
                                      serviceType === "Transfer of Equity";
                                    const isFirstRegistration =
                                      serviceType === "First_Registration" ||
                                      serviceType === "First Registration";
                                    const isReviewOfLegalPack =
                                      serviceType === "Review_Legal_Pack" ||
                                      serviceType === "Review of Legal Pack";
                                    const isSale = serviceType === "Sale";

                                    if (isTransferOfEquity) {
                                      // Transfer of Equity: Include VAT from searches
                                      return (
                                        (landRegistrySearch.vat > 0
                                          ? landRegistrySearch.vat
                                          : 0) +
                                        (landChargesSearch.vat > 0
                                          ? landChargesSearch.vat
                                          : 0) +
                                        (officialCopies.vat > 0
                                          ? officialCopies.vat
                                          : 0)
                                      );
                                    } else if (isFirstRegistration) {
                                      // First Registration: No VAT on disbursements
                                      return 0;
                                    } else if (
                                      isMortgageRedemption ||
                                      isRemortgage ||
                                      isReviewOfLegalPack
                                    ) {
                                      return officialCopies.net > 0
                                        ? officialCopies.vat
                                        : 0;
                                    } else if (isSale) {
                                      return officialCopies.net > 0
                                        ? officialCopies.vat
                                        : 0;
                                    } else {
                                      return (
                                        landRegistrySearch.vat +
                                        landChargesSearch.vat
                                      );
                                    }
                                  })(),
                              )}
                            </td>
                            <td className="text-end">
                              {formatCurrency(
                                calculationTotals?.disbursementCost?.gross ||
                                  (() => {
                                    const serviceType =
                                      leadData?.service_type ||
                                      legalFeeData?.service_type ||
                                      "";
                                    const isMortgageRedemption =
                                      serviceType === "Mortgage_Redemption" ||
                                      serviceType === "Mortgage Redemption";
                                    const isRemortgage =
                                      serviceType === "Remortgage";
                                    const isTransferOfEquity =
                                      serviceType === "Transfer_of_Equity" ||
                                      serviceType === "Transfer of Equity";
                                    const isFirstRegistration =
                                      serviceType === "First_Registration" ||
                                      serviceType === "First Registration";
                                    const isReviewOfLegalPack =
                                      serviceType === "Review_Legal_Pack" ||
                                      serviceType === "Review of Legal Pack";
                                    const isSale = serviceType === "Sale";

                                    if (isTransferOfEquity) {
                                      // Transfer of Equity: Include all disbursements that may exist
                                      const localSearchIns = getFeeWithVAT(
                                        "localSearchInsurance",
                                        false,
                                      );
                                      return (
                                        (stampDuty.gross > 0
                                          ? stampDuty.gross
                                          : 0) +
                                        (searchPack.gross > 0
                                          ? searchPack.gross
                                          : 0) +
                                        (localSearchIns.gross > 0
                                          ? localSearchIns.gross
                                          : 0) +
                                        (landRegistrySearch.gross > 0
                                          ? landRegistrySearch.gross
                                          : 0) +
                                        (landChargesSearch.gross > 0
                                          ? landChargesSearch.gross
                                          : 0) +
                                        (officialCopies.gross > 0
                                          ? officialCopies.gross
                                          : 0)
                                      );
                                    } else if (isFirstRegistration) {
                                      // First Registration: HM Land Registry Fee + Land Registry Search
                                      return (
                                        hmLandRegistryFee.gross +
                                        landRegistrySearch.gross
                                      );
                                    } else if (
                                      isMortgageRedemption ||
                                      isRemortgage ||
                                      isReviewOfLegalPack
                                    ) {
                                      return officialCopies.net > 0
                                        ? officialCopies.gross
                                        : 0;
                                    } else if (isSale) {
                                      return officialCopies.net > 0
                                        ? officialCopies.gross
                                        : 0;
                                    } else {
                                      return (
                                        (stampDuty.net > 0
                                          ? stampDuty.gross
                                          : 0) +
                                        hmLandRegistryFee.gross +
                                        landRegistrySearch.gross +
                                        landChargesSearch.gross +
                                        searchPack.gross +
                                        (officialCopies.net > 0
                                          ? officialCopies.gross
                                          : 0)
                                      );
                                    }
                                  })(),
                              )}
                            </td>
                          </tr>
                        </>
                      )}

                    {/* Grand Total */}
                    <tr
                      style={{
                        backgroundColor: "#172554",
                        fontWeight: "700",
                        color: "white",
                      }}
                    >
                      <td style={{ color: "white", fontWeight: "700" }}>
                        TOTAL (Legal Cost + Disbursement Cost)
                      </td>
                      <td className="text-end" style={{ color: "white" }}>
                        {formatCurrency(
                          calculationTotals?.total?.net ||
                            (leadData?.service_type === "Sale"
                              ? legalFee +
                                ttFee +
                                identityCheck +
                                (isTruthy(
                                  leadData?.info?.sellingWithMortgage,
                                ) || isTruthy(leadData?.selling_with_mortgage)
                                  ? mortgageAdminFee.net
                                  : 0) +
                                (leaseholdFee.net > 0 &&
                                leadData?.ownership_type === "Leasehold"
                                  ? leaseholdFee.net
                                  : 0) +
                                (isTruthy(leadData?.info?.sharedOwnership) ||
                                isTruthy(leadData?.shared_ownership)
                                  ? sharedOwnershipFee.net
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.auctionRepossession,
                                ) || isTruthy(leadData?.auction_repossession)
                                  ? auctionRepossessionFee.net
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.arrangingIndemnityPolicy,
                                ) ||
                                isTruthy(leadData?.arranging_indemnity_policy)
                                  ? arrangingIndemnityPolicy.net
                                  : 0) +
                                (officialCopies.net > 0
                                  ? officialCopies.net
                                  : 0)
                              : legalFee +
                                ttFee +
                                identityCheck +
                                (isTruthy(leadData?.info?.buyingWithMortgage) ||
                                isTruthy(leadData?.buying_with_mortgage)
                                  ? mortgageAdminFee.net
                                  : 0) +
                                (leaseholdFee.net > 0 &&
                                leadData?.ownership_type === "Leasehold"
                                  ? leaseholdFee.net
                                  : 0) +
                                (helpToBuyISAFee.net > 0
                                  ? helpToBuyISAFee.net
                                  : 0) +
                                (isTruthy(leadData?.info?.helpToBuy) ||
                                isTruthy(leadData?.help_to_buy)
                                  ? helpToBuyFee.net
                                  : 0) +
                                (isTruthy(leadData?.info?.rightToBuy) ||
                                isTruthy(leadData?.right_to_buy)
                                  ? rightToBuyFee.net
                                  : 0) +
                                (isTruthy(leadData?.info?.islamicMortgage) ||
                                isTruthy(leadData?.islamic_mortgage)
                                  ? islamicMortgageFee.net
                                  : 0) +
                                (isTruthy(leadData?.info?.giftedDeposit) ||
                                isTruthy(leadData?.gifted_deposit)
                                  ? giftedDepositFeeFirstGiftor.net
                                  : 0) +
                                (isTruthy(leadData?.info?.buyToLet) ||
                                isTruthy(leadData?.buy_to_let)
                                  ? buyToLetFee.net
                                  : 0) +
                                (isTruthy(leadData?.info?.sharedOwnership) ||
                                isTruthy(leadData?.shared_ownership)
                                  ? sharedOwnershipFee.net
                                  : 0) +
                                (isTruthy(leadData?.info?.newBuild) ||
                                isTruthy(leadData?.new_build)
                                  ? newBuildFee.net
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.auctionRepossession,
                                ) || isTruthy(leadData?.auction_repossession)
                                  ? auctionRepossessionFee.net
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.arrangingIndemnityPolicy,
                                ) ||
                                isTruthy(leadData?.arranging_indemnity_policy)
                                  ? arrangingIndemnityPolicy.net
                                  : 0) +
                                (stampDuty.net > 0 ? stampDuty.net : 0) +
                                hmLandRegistryFee.net +
                                landRegistrySearch.net +
                                landChargesSearch.net +
                                searchPack.net +
                                (officialCopies.net > 0
                                  ? officialCopies.net
                                  : 0)),
                        )}
                      </td>
                      <td
                        className="text-end"
                        style={{ color: "white", fontWeight: "700" }}
                      >
                        {formatCurrency(
                          calculationTotals?.total?.vat ||
                            (leadData?.service_type === "Sale"
                              ? legalFeeVAT +
                                ttFeeVAT +
                                identityCheckVAT +
                                (isTruthy(
                                  leadData?.info?.sellingWithMortgage,
                                ) || isTruthy(leadData?.selling_with_mortgage)
                                  ? mortgageAdminFee.vat
                                  : 0) +
                                (leaseholdFee.net > 0 &&
                                leadData?.ownership_type === "Leasehold"
                                  ? leaseholdFee.vat
                                  : 0) +
                                (isTruthy(leadData?.info?.sharedOwnership) ||
                                isTruthy(leadData?.shared_ownership)
                                  ? sharedOwnershipFee.vat
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.auctionRepossession,
                                ) || isTruthy(leadData?.auction_repossession)
                                  ? auctionRepossessionFee.vat
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.arrangingIndemnityPolicy,
                                ) ||
                                isTruthy(leadData?.arranging_indemnity_policy)
                                  ? arrangingIndemnityPolicy.vat
                                  : 0) +
                                (officialCopies.net > 0
                                  ? officialCopies.vat
                                  : 0)
                              : legalFeeVAT +
                                ttFeeVAT +
                                identityCheckVAT +
                                (isTruthy(leadData?.info?.buyingWithMortgage) ||
                                isTruthy(leadData?.buying_with_mortgage)
                                  ? mortgageAdminFee.vat
                                  : 0) +
                                (leaseholdFee.net > 0 &&
                                leadData?.ownership_type === "Leasehold"
                                  ? leaseholdFee.vat
                                  : 0) +
                                (helpToBuyISAFee.net > 0
                                  ? helpToBuyISAFee.vat
                                  : 0) +
                                (isTruthy(leadData?.info?.helpToBuy) ||
                                isTruthy(leadData?.help_to_buy)
                                  ? helpToBuyFee.vat
                                  : 0) +
                                (isTruthy(leadData?.info?.rightToBuy) ||
                                isTruthy(leadData?.right_to_buy)
                                  ? rightToBuyFee.vat
                                  : 0) +
                                (isTruthy(leadData?.info?.islamicMortgage) ||
                                isTruthy(leadData?.islamic_mortgage)
                                  ? islamicMortgageFee.vat
                                  : 0) +
                                (isTruthy(leadData?.info?.giftedDeposit) ||
                                isTruthy(leadData?.gifted_deposit)
                                  ? giftedDepositFeeFirstGiftor.vat
                                  : 0) +
                                (isTruthy(leadData?.info?.buyToLet) ||
                                isTruthy(leadData?.buy_to_let)
                                  ? buyToLetFee.vat
                                  : 0) +
                                (isTruthy(leadData?.info?.sharedOwnership) ||
                                isTruthy(leadData?.shared_ownership)
                                  ? sharedOwnershipFee.vat
                                  : 0) +
                                (isTruthy(leadData?.info?.newBuild) ||
                                isTruthy(leadData?.new_build)
                                  ? newBuildFee.vat
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.auctionRepossession,
                                ) || isTruthy(leadData?.auction_repossession)
                                  ? auctionRepossessionFee.vat
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.arrangingIndemnityPolicy,
                                ) ||
                                isTruthy(leadData?.arranging_indemnity_policy)
                                  ? arrangingIndemnityPolicy.vat
                                  : 0) +
                                landRegistrySearch.vat +
                                landChargesSearch.vat),
                        )}
                      </td>
                      <td
                        className="text-end"
                        style={{ color: "white", fontWeight: "700" }}
                      >
                        {formatCurrency(
                          calculationTotals?.total?.gross ||
                            (leadData?.service_type === "Sale"
                              ? legalFeeGross +
                                ttFeeGross +
                                identityCheckGross +
                                (isTruthy(
                                  leadData?.info?.sellingWithMortgage,
                                ) || isTruthy(leadData?.selling_with_mortgage)
                                  ? mortgageAdminFee.gross
                                  : 0) +
                                (leaseholdFee.net > 0 &&
                                leadData?.ownership_type === "Leasehold"
                                  ? leaseholdFee.gross
                                  : 0) +
                                (isTruthy(leadData?.info?.sharedOwnership) ||
                                isTruthy(leadData?.shared_ownership)
                                  ? sharedOwnershipFee.gross
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.auctionRepossession,
                                ) || isTruthy(leadData?.auction_repossession)
                                  ? auctionRepossessionFee.gross
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.arrangingIndemnityPolicy,
                                ) ||
                                isTruthy(leadData?.arranging_indemnity_policy)
                                  ? arrangingIndemnityPolicy.gross
                                  : 0) +
                                (officialCopies.net > 0
                                  ? officialCopies.gross
                                  : 0)
                              : legalFeeGross +
                                ttFeeGross +
                                identityCheckGross +
                                (isTruthy(leadData?.info?.buyingWithMortgage) ||
                                isTruthy(leadData?.buying_with_mortgage)
                                  ? mortgageAdminFee.gross
                                  : 0) +
                                (leaseholdFee.net > 0 &&
                                leadData?.ownership_type === "Leasehold"
                                  ? leaseholdFee.gross
                                  : 0) +
                                (helpToBuyISAFee.net > 0
                                  ? helpToBuyISAFee.gross
                                  : 0) +
                                (isTruthy(leadData?.info?.helpToBuy) ||
                                isTruthy(leadData?.help_to_buy)
                                  ? helpToBuyFee.gross
                                  : 0) +
                                (isTruthy(leadData?.info?.rightToBuy) ||
                                isTruthy(leadData?.right_to_buy)
                                  ? rightToBuyFee.gross
                                  : 0) +
                                (isTruthy(leadData?.info?.islamicMortgage) ||
                                isTruthy(leadData?.islamic_mortgage)
                                  ? islamicMortgageFee.gross
                                  : 0) +
                                (isTruthy(leadData?.info?.giftedDeposit) ||
                                isTruthy(leadData?.gifted_deposit)
                                  ? giftedDepositFeeFirstGiftor.gross
                                  : 0) +
                                (isTruthy(leadData?.info?.buyToLet) ||
                                isTruthy(leadData?.buy_to_let)
                                  ? buyToLetFee.gross
                                  : 0) +
                                (isTruthy(leadData?.info?.sharedOwnership) ||
                                isTruthy(leadData?.shared_ownership)
                                  ? sharedOwnershipFee.gross
                                  : 0) +
                                (isTruthy(leadData?.info?.newBuild) ||
                                isTruthy(leadData?.new_build)
                                  ? newBuildFee.gross
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.auctionRepossession,
                                ) || isTruthy(leadData?.auction_repossession)
                                  ? auctionRepossessionFee.gross
                                  : 0) +
                                (isTruthy(
                                  leadData?.info?.arrangingIndemnityPolicy,
                                ) ||
                                isTruthy(leadData?.arranging_indemnity_policy)
                                  ? arrangingIndemnityPolicy.gross
                                  : 0) +
                                (stampDuty.net > 0 ? stampDuty.gross : 0) +
                                hmLandRegistryFee.gross +
                                landRegistrySearch.gross +
                                landChargesSearch.gross +
                                searchPack.gross +
                                (officialCopies.net > 0
                                  ? officialCopies.gross
                                  : 0)),
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>

                {/* Disclaimer & Informational Sections - Hide for Grant Application */}
                {leadData?.service_type !== "Grant_Application" &&
                  leadData?.service_type !== "Grant Application" && (
                    <>
                      <div
                        className="mt-3"
                        style={{
                          fontSize: "14px",
                          color: "#666",
                          lineHeight: "1.6",
                        }}
                      >
                        <p>
                          We endeavour to complete the transaction within the
                          cost summary based on information provided by you. In
                          certain cases, additional work needs to be carried
                          which may attract additional legal cost.
                        </p>
                      </div>
                      {/* Searches Section */}
                      <div
                        className="p-3 mb-3 mt-4"
                        style={{
                          backgroundColor: "#f8f9fa",
                          borderLeft: "4px solid #d32f2f",
                          borderRadius: "5px",
                        }}
                      >
                        <h6 className="mb-2">Searches</h6>
                        <p style={{ fontSize: "14px", lineHeight: "1.6" }}>
                          Our quotation includes the price for the full suite of
                          searches; local, drainage & water, environmental,
                          chancel and coal mining. We always recommend the full
                          suite of searches is carried out and is a requirement
                          of all mortgage lenders. It may be necessary to obtain
                          additional searches as recommended or in certain
                          circumstances where your it is a requirement of your
                          mortgage lender. If this becomes apparent we will
                          inform you prior to undertaking any additional
                          searches.
                        </p>
                      </div>

                      {/* Leasehold Section */}
                      <div
                        className="p-3 mb-3"
                        style={{
                          backgroundColor: "#f8f9fa",
                          borderLeft: "4px solid #d32f2f",
                          borderRadius: "5px",
                        }}
                      >
                        <h6 className="mb-2">Leasehold</h6>
                        <p style={{ fontSize: "14px", lineHeight: "1.6" }}>
                          The purchase of a Leasehold property often involves
                          additional work liaising with the landlord and/or
                          managing agent for which an additional fixed fee is
                          payable. There are often enquiries to be raised on
                          completion of the reading of transfer and charge. The
                          fees payable to the landlord and/or managing agent can
                          only be ascertained when enquiries are raised during
                          the transaction and will vary but in most cases will
                          be between £50.00 and £300.00
                        </p>
                      </div>

                      {/* Help to Buy Section */}
                      <div
                        className="p-3 mb-3"
                        style={{
                          backgroundColor: "#f8f9fa",
                          borderLeft: "4px solid #d32f2f",
                          borderRadius: "5px",
                        }}
                      >
                        <h6 className="mb-2">Help to Buy</h6>
                        <p style={{ fontSize: "14px", lineHeight: "1.6" }}>
                          Help to Buy is a government backed scheme to help you
                          buy a property by either providing you with an equity
                          loan or a mortgage guarantee. As part of the
                          conveyancing process we will be required to deal with
                          the Help to Buy provider and comply with their
                          formalities and timescales, this entails a substantial
                          amount of additional work for which an additional
                          fixed fee is payable.
                        </p>
                      </div>

                      {/* New Build Properties Section */}
                      <div
                        className="p-3 mb-3"
                        style={{
                          backgroundColor: "#f8f9fa",
                          borderLeft: "4px solid #d32f2f",
                          borderRadius: "5px",
                        }}
                      >
                        <h6 className="mb-2">New Build Properties</h6>
                        <p style={{ fontSize: "14px", lineHeight: "1.6" }}>
                          When buying a new build property, whether under one of
                          the above schemes or not, an engrossment fee maybe
                          payable to the seller or developers solicitor. The fee
                          can range from £60 - £120+ VAT. As part of the
                          conveyancing process we will be required to deal with
                          the building developers solicitor or legal
                          representatives, this entails an additional work for
                          which an additional fixed fee is payable.
                        </p>
                      </div>

                      {/* Mortgaged Section */}
                      <div
                        className="p-3 mb-3"
                        style={{
                          backgroundColor: "#f8f9fa",
                          borderLeft: "4px solid #d32f2f",
                          borderRadius: "5px",
                        }}
                      >
                        <h6 className="mb-2">Mortgaged</h6>
                        <p style={{ fontSize: "14px", lineHeight: "1.6" }}>
                          If you are buying a property with the assistance of a
                          mortgage we are often required to act on the mortgage
                          lender's behalf as well as yours and an additional fee
                          for dealing with these formalities is charged.
                        </p>
                      </div>

                      {/* Identification Section */}
                      <div
                        className="p-3 mb-3"
                        style={{
                          backgroundColor: "#f8f9fa",
                          borderLeft: "4px solid #d32f2f",
                          borderRadius: "5px",
                        }}
                      >
                        <h6 className="mb-2">Identification</h6>
                        <p style={{ fontSize: "14px", lineHeight: "1.6" }}>
                          In order to comply with Money Laundering regulations
                          we are required to verify the identity of all clients
                          we act for, we therefore carry out an online
                          identification verification service. Money Laundering
                          - In order to comply with Money Laundering
                          legislation, we are required to verify the identity of
                          all clients. You will be asked to provide original
                          copies or certified copies of your Identification.
                          These can be brought into the office for copying or
                          posted to us.
                        </p>
                      </div>

                      {/* SDLT Thresholds Section */}
                      <div
                        className="p-3 mb-3"
                        style={{
                          backgroundColor: "#f8f9fa",
                          borderLeft: "4px solid #d32f2f",
                          borderRadius: "5px",
                        }}
                      >
                        <h6 className="mb-2">SDLT Thresholds</h6>
                        <p style={{ fontSize: "14px", lineHeight: "1.6" }}>
                          Although you may be eligible to the stamp duty holiday
                          that has been implemented in the recent budget we
                          strongly advise you set funds aside for the full stamp
                          duty payment as we cannot guarantee any completion
                          will take place within these time frames.For
                          completions from the 1st July 2020 until the 31st
                          March 2021 there is an SDLT payment for properties up
                          to £500,000 or below.For completions from 1st April
                          2021 up until the 30th June 2021 there is an SDLT
                          payment on properties between £250,001 to £500,000 or
                          below.For completions from 1st July 2021 to 30th
                          September 2021 there's no SDLT to pay for properties
                          at £250,000 or below.For completions from the 1st
                          October 2021 the SDLT payment threshold returns or
                          they were before the 8th July 2020, subject to any
                          further changes to the budget. Please note this SDLT
                          holiday only relates where you are replacing your main
                          residence or the property purchasing is not an
                          additional property.
                        </p>
                      </div>
                    </>
                  )}
              </>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={downloadQuote}>
          <i className="bi bi-download"></i> Download
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewQuoteDetailModal;
